// Trip date cascades in flight: serialized, and outliving whoever started one.
//
// A cascade is the TAIL of a save that has already landed. The `days` (or
// `date`) the user typed is stored; the sibling dates it implies are merely not
// written yet. So it cannot be tied to a page's lifetime -- typing Days and
// clicking straight through to the trip is the commonest way to make one, and
// under autosave that edit only flushes AS the page unmounts. A cascade
// cancelled on unmount is a cascade that never runs at all, leaving the user
// looking at exactly the schedule they just corrected. Same rule as the save
// queue itself: the write outlives the editor.
//
// Serialized module-wide (not per page) for the reason cascadeLegDates re-dates
// relative to what is STORED: two runs computing from the same read would fight
// over the same rows. Chaining means the second sees what the first wrote.
//
// A leaf module rather than part of TripSchedule.mjs, which is where the rest of
// this lives and where callers import it from (it re-exports all of this): only
// the state and the promise plumbing are here, and setupTests.js has to import
// them from somewhere that does NOT reach Database.mjs. Database builds its
// Amplify client at module load, and a setup file that pulls that in gets the
// real one -- before any test file's jest.mock('aws-amplify/api') exists.

let cascadeChain = Promise.resolve();
let cascadesQueued = 0;

// Queue `work` behind every cascade already running. Returns ITS promise --
// rejections belong to the caller, which is the page that can report them.
export function runLegCascade(work) {
  cascadesQueued += 1;
  const run = cascadeChain.then(() => work());
  // The chain itself must never reject: a rejected chain would skip every
  // cascade queued behind it (and hand them all the same failure).
  cascadeChain = run.then(() => {}, () => {}).then(() => { cascadesQueued -= 1; });
  return run;
}

// Resolve once every cascade queued SO FAR has finished writing. Never rejects.
//
// For pages that read legs: a cascade writes straight through Database.updateTrip
// rather than queueing a save-queue job, so `saveQueue.settled` cannot see it,
// and a page reached from the leg that caused one (the group page, one click up
// the breadcrumb) would otherwise fetch the dates the cascade is in the middle
// of replacing. Wait on the LANES FIRST and call this after: the cascade is
// queued from the save's onSaved, which the queue runs before it notifies the
// subscribers a lane wait resolves on -- so a chain snapshot taken any earlier
// misses the very cascade the reader is waiting for.
export function settledLegCascades() {
  return cascadeChain;
}

// Is anything cascading right now? For the "is a handed-over snapshot worth
// painting" check, which has to be answered without awaiting anything.
export function legCascadesPending() {
  return cascadesQueued > 0;
}

// Tests only: module-level state survives Jest's resetMocks, so setupTests.js
// calls this after each test. A cascade a test left in flight would otherwise
// be something the NEXT test's page waits on -- and its writes would go to an
// amplify mock that has since been reset.
export function _resetLegCascadesForTests() {
  cascadeChain = Promise.resolve();
  cascadesQueued = 0;
}
